import { GHOST_STYLE, ghostSvgAttributes } from '../../plugin-sdk/ghost_preview.mjs';
import { S } from '../app/app_state.js?v=20260817c';
import {
  getPlanSvg,
  svgEl,
  worldToScreen,
  screenToWorld,
  setStatus
} from '../render/plan_renderer.js?v=20260904f';
import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { makeId } from '../utils/id_service.js?v=20260706d';
import { recordUndo } from '../services/history_service.js?v=20260903c';
import {
  clearDrawingObjects,
  registerDrawingObject
} from '../objects/drawing_object_registry.js?v=20260904f';

const DEFAULT_SIZE_MM = 1000;

// --- Project state ---

function ensureBoxes(stateKey) {
  if (!S.project) return [];
  if (!Array.isArray(S.project[stateKey])) S.project[stateKey] = [];
  return S.project[stateKey];
}

// --- UI placement state ---

function getActiveTool(uiKey) {
  return S.ui?.[uiKey + '_t'] ?? null;
}

function getPreview(uiKey) {
  return S.ui?.[uiKey + '_p'] ?? null;
}

function setPreview(uiKey, value) {
  if (S.ui) S.ui[uiKey + '_p'] = value;
}

function activatePlacementTool(uiKey, label) {
  if (S.ui) {
    S.ui[uiKey + '_t'] = true;
    S.ui[uiKey + '_p'] = null;
  }
  setStatus(`${label} – klicken zum Einfügen, Esc zum Abbrechen`);
  AppRuntime.requestPlanRender('ph-activate');
}

function deactivatePlacementTool(uiKey) {
  if (S.ui) {
    S.ui[uiKey + '_t'] = null;
    S.ui[uiKey + '_p'] = null;
  }
  getPlanSvg()?.querySelector('.ph-ghost')?.remove();
  setStatus('Bereit');
  AppRuntime.requestPlanRender('ph-cancel');
}

// --- Pointer world ---

function pointerWorld(event) {
  const svg = getPlanSvg();
  if (!svg) return null;
  const rect = svg.getBoundingClientRect();
  return screenToWorld(event.clientX - rect.left, event.clientY - rect.top);
}

// --- DrawingObject sync ---

function placeholderDrawingObjectId(boxId) {
  return `ph-do-${boxId}`;
}

function syncPlaceholderDrawingObjects(systemId, stateKey) {
  clearDrawingObjects({ systemType: systemId });
  const objects = [];
  for (const box of ensureBoxes(stateKey)) {
    const hw = box.w / 2;
    const hd = box.d / 2;
    const object = registerDrawingObject({
      id: placeholderDrawingObjectId(box.id),
      type: 'placeholder-box',
      systemType: systemId,
      position: { x: box.x + hw, z: box.z + hd },
      rotation: box.rotation || 0,
      metadata: {
        boxId: box.id,
        coreGroupId: box.id,
        sourceId: box.id,
        stateKey,
        label: '1 × 1 m',
        localBounds: { minX: -hw, minZ: -hd, maxX: hw, maxZ: hd }
      },
      selectable: true,
      movable: true,
      rotatable: true,
      deletable: true,
      copyable: true,
      getState() {
        const b = ensureBoxes(stateKey).find(item => item.id === box.id);
        if (!b) return null;
        return {
          id: placeholderDrawingObjectId(b.id),
          type: 'placeholder-box',
          systemType: systemId,
          position: { x: b.x + b.w / 2, z: b.z + b.d / 2 },
          rotation: b.rotation || 0,
          metadata: {
            boxId: b.id,
            coreGroupId: b.id,
            sourceId: b.id,
            stateKey,
            label: '1 × 1 m',
            localBounds: { minX: -b.w / 2, minZ: -b.d / 2, maxX: b.w / 2, maxZ: b.d / 2 }
          }
        };
      },
      setState(state = {}) {
        const b = ensureBoxes(stateKey).find(item => item.id === box.id);
        if (!b) return false;
        if (Number.isFinite(Number(state.position?.x))) b.x = state.position.x - b.w / 2;
        if (Number.isFinite(Number(state.position?.z))) b.z = state.position.z - b.d / 2;
        if (Number.isFinite(Number(state.rotation))) b.rotation = state.rotation;
        return true;
      },
      remove() {
        const arr = ensureBoxes(stateKey);
        const idx = arr.findIndex(b => b.id === box.id);
        if (idx !== -1) arr.splice(idx, 1);
        return true;
      }
    });
    if (object) objects.push(object);
  }
  return objects;
}

// --- 2D rendering ---

function drawPlaceholderBoxes2D(parentLayer, stateKey) {
  const boxes = ensureBoxes(stateKey);
  if (!boxes.length) return;
  const scale = S.camera2D?.scale || 1;
  for (const box of boxes) {
    const { x: cx, y: cy } = worldToScreen(box.x + box.w / 2, box.z + box.d / 2);
    const sw = box.w * scale;
    const sd = box.d * scale;
    const g = svgEl('g', {
      transform: `translate(${cx},${cy}) rotate(${box.rotation || 0})`,
      'data-object-id': placeholderDrawingObjectId(box.id)
    }, parentLayer);
    svgEl('rect', {
      x: -sw / 2,
      y: -sd / 2,
      width: sw,
      height: sd,
      fill: '#cccccc',
      stroke: '#888888',
      'stroke-width': 1
    }, g);
  }
}

function drawPlaceholderGhost(uiKey) {
  const preview = getPreview(uiKey);
  const svg = getPlanSvg();
  if (!preview || !svg) return;
  svg.querySelector('.ph-ghost')?.remove();
  const scale = S.camera2D?.scale || 1;
  const { x: cx, y: cy } = worldToScreen(preview.x + preview.w / 2, preview.z + preview.d / 2);
  const sw = preview.w * scale;
  const sd = preview.d * scale;
  const g = svgEl('g', { class: 'ph-ghost', opacity: GHOST_STYLE.opacity, 'pointer-events': 'none' }, svg);
  svgEl('rect', {
    x: cx - sw / 2,
    y: cy - sd / 2,
    width: sw,
    height: sd,
    ...ghostSvgAttributes(), opacity: 1
  }, g);
}

// --- 3D rendering ---

function buildPlaceholderBoxes3D(ctx, stateKey) {
  const boxes = S.project?.[stateKey];
  if (!Array.isArray(boxes) || !boxes.length) return;
  const THREE = ctx.THREE;
  const material = new THREE.MeshStandardMaterial({ color: 0xbbbbbb, roughness: 0.8, metalness: 0 });
  for (const box of boxes) {
    const w = (box.w || DEFAULT_SIZE_MM) / 1000;
    const d = (box.d || DEFAULT_SIZE_MM) / 1000;
    const h = 1.0;
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
    mesh.userData = {
      ...(mesh.userData || {}),
      objectId: placeholderDrawingObjectId(box.id),
      groupId: box.id
    };
    mesh.position.set(
      (box.x + box.w / 2) / 1000,
      h / 2,
      (box.z + box.d / 2) / 1000
    );
    if (box.rotation) mesh.rotation.y = (box.rotation * Math.PI) / 180;
    ctx.addObject(mesh);
  }
}

/**
 * Ergänzt ein Basic-System um einen 1×1-m-Platzhalter-Zeichnen-Workflow.
 * Toolbar-Button → Ghost am Cursor → Klick platziert graue Fläche (2D) / grauer Würfel (3D).
 *
 * @param {object} basicSystem - Ergebnis von createBasicProductSystem oder gleichwertig
 * @param {{ label?: string }} [options]
 */
export function withPlaceholderDrawing(basicSystem, { label = 'Zeichnen' } = {}) {
  const systemId = basicSystem.id;
  const stateKey = systemId.replace(/-/g, '_') + '_ph';
  const uiKey = stateKey;

  return {
    ...basicSystem,
    toolbarButtons: [
      {
        id: `btn_ph_${systemId.replace(/-/g, '_')}`,
        action: 'draw',
        label,
        group: 'ph_draw',
        groupLabel: 'Zeichnen'
      }
    ],

    render2D(ctx) {
      syncPlaceholderDrawingObjects(systemId, stateKey);
      drawPlaceholderBoxes2D(ctx.parentLayer, stateKey);
    },

    getDrawingObjects() {
      return syncPlaceholderDrawingObjects(systemId, stateKey);
    },

    build3D(ctx) {
      buildPlaceholderBoxes3D(ctx, stateKey);
    },

    handleToolbarAction(action) {
      if (action !== 'draw') return basicSystem.handleToolbarAction?.(action) ?? false;
      if (getActiveTool(uiKey)) {
        deactivatePlacementTool(uiKey);
      } else {
        activatePlacementTool(uiKey, label);
      }
      return true;
    },

    handlePointerMove(ctx) {
      const event = ctx?.event;
      if (!event || (ctx?.currentView && ctx.currentView !== 'plan')) return false;
      if (!getActiveTool(uiKey)) return false;
      const world = pointerWorld(event);
      if (!world) return false;
      setPreview(uiKey, {
        x: world.x - DEFAULT_SIZE_MM / 2,
        z: world.z - DEFAULT_SIZE_MM / 2,
        w: DEFAULT_SIZE_MM,
        d: DEFAULT_SIZE_MM
      });
      AppRuntime.requestPlanRender('ph-preview');
      return true;
    },

    handlePointerDown(ctx) {
      const event = ctx?.event;
      if (!event || (ctx?.currentView && ctx.currentView !== 'plan')) return false;
      if (!getActiveTool(uiKey)) return false;
      if (event.button !== 0) return false;
      const world = pointerWorld(event);
      if (!world) return false;
      event.preventDefault?.();
      event.stopPropagation?.();
      const box = {
        id: makeId('ph'),
        x: world.x - DEFAULT_SIZE_MM / 2,
        z: world.z - DEFAULT_SIZE_MM / 2,
        w: DEFAULT_SIZE_MM,
        d: DEFAULT_SIZE_MM,
        rotation: 0
      };
      recordUndo(`${label} einfügen`);
      ensureBoxes(stateKey).push(box);
      syncPlaceholderDrawingObjects(systemId, stateKey);
      setPreview(uiKey, { x: box.x, z: box.z, w: box.w, d: box.d });
      AppRuntime.requestAppRefresh(false, 'ph-placed');
      return true;
    },

    drawInteractionOverlay() {
      drawPlaceholderGhost(uiKey);
    },

    wantsPointerDownBeforeSelection() {
      return Boolean(getActiveTool(uiKey));
    },

    cancelInteraction(ctx) {
      if (!getActiveTool(uiKey) && !getPreview(uiKey)) return false;
      ctx?.event?.preventDefault?.();
      deactivatePlacementTool(uiKey);
      return true;
    },

    isToolbarActionActive(action) {
      return action === 'draw' && Boolean(getActiveTool(uiKey));
    }
  };
}
